import * as ReactNativeKeychain from "~/ui/keychain"
import * as ExpoSecureStore from "expo-secure-store"

/**
 * A tiny key/value secure storage wrapper backed by react-native-keychain.
 * Uses `service` per key so keys don't overwrite each other.
 */
export const secureStorage = {
  async isAvailableAsync(): Promise<boolean> {
    try {
      // If Keychain APIs are callable, we treat it as available.
      return typeof ReactNativeKeychain.getGenericPassword === "function"
    } catch {
      return false
    }
  },

  async getItemAsync(key: string): Promise<string | null> {
    try {
      const creds = await ReactNativeKeychain.getGenericPassword({ service: key })
      if (creds && typeof creds === "object") return creds.password ?? null
      return null
    } catch {
      return null
    }
  },

  async setItemAsync(key: string, value: string): Promise<void> {
    // store value in password field; username just a placeholder
    await ReactNativeKeychain.setGenericPassword("kv", value ?? "", { service: key })
  },

  async deleteItemAsync(key: string): Promise<void> {
    try {
      await ReactNativeKeychain.resetGenericPassword({ service: key })
    } catch {
      // ignore
    }
  },
}

export async function tryMigrateFromExpoSecureStore(keys: string[]) {
  const SecureStore = ExpoSecureStore as {
    getItemAsync: (key: string) => Promise<string | null>
    setItemAsync: (key: string, value: string) => Promise<void>
    deleteItemAsync?: (key: string) => Promise<void>
  }
  if (!SecureStore?.getItemAsync) return

  for (const key of keys) {
    try {
      const cur = await secureStorage.getItemAsync(key)
      if (cur) continue
      const old = await SecureStore.getItemAsync(key)
      if (!old) continue
      await secureStorage.setItemAsync(key, old)
      if (SecureStore.deleteItemAsync) {
        try {
          await SecureStore.deleteItemAsync(key)
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    }
  }
}

